import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import KeyboardVoiceRoundedIcon from '@mui/icons-material/KeyboardVoiceRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Button, Chip, Divider, Drawer, IconButton, LinearProgress, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { ActivityIconKey, grammarTargets, TargetSource, TeacherActivity, TeacherPrompt, vocabularyTargets } from '../mockLesson';
import { aiButtonSx, BLUE, BLUE_TEXT, BORDER, cardSx, GREEN, INTER, MUTED, NAVY, POPPINS, primaryButtonSx, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_SUBTLE, SURFACE_TINT, WARNING_TEXT, YELLOW } from '../teacherStyles';

const activityIcons: Record<ActivityIconKey, React.ElementType> = {
    image: ImageOutlinedIcon,
    words: MenuBookRoundedIcon,
    listen: VolumeUpRoundedIcon,
    speak: RecordVoiceOverRoundedIcon,
    check: CheckCircleRoundedIcon,
};

interface TeacherBuilderProps {
    activities: TeacherActivity[];
    selectedActivity: TeacherActivity | null;
    onSelectActivity: (activity: TeacherActivity) => void;
    onSelectLanguage: () => void;
    onBack: () => void;
    onFinish: () => void;
}

interface LessonOutlinePanelProps extends Omit<TeacherBuilderProps, 'onBack' | 'onFinish'> {
    selectedPromptId: number | null;
    onSelectPrompt: (activity: TeacherActivity, prompt: TeacherPrompt) => void;
    onAction: (message: string) => void;
    embedded?: boolean;
}

function LessonOutlinePanel({ activities, selectedActivity, onSelectActivity, onSelectLanguage, selectedPromptId, onSelectPrompt, onAction, embedded = false }: LessonOutlinePanelProps) {
    const totalMinutes = activities.reduce((sum, activity) => sum + activity.duration, 0);
    const totalPrompts = activities.reduce((sum, activity) => sum + activity.prompts.length, 0);

    return (
        <Box component="nav" aria-label="Self-study lesson structure" sx={{ minWidth: 0, minHeight: 0, height: '100%', display: embedded ? 'flex' : { xs: 'none', md: 'flex' }, flexDirection: 'column', backgroundColor: '#fff', borderRight: embedded ? 0 : `1px solid ${BORDER}` }}>
            <Box sx={{ p: 2.5, pb: 2 }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, letterSpacing: '0.08em' }}>COURSE</Typography>
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: NAVY, fontSize: 12.5, mt: 0.35 }}>Intermediate Mandarin II</Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25 }}>
                    <ChevronRightRoundedIcon sx={{ color: BLUE, fontSize: 16 }} />
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, letterSpacing: '0.08em' }}>ASSIGNMENT <Box component="span" sx={{ fontWeight: 500, color: MUTED }}>· Lesson</Box></Typography>
                </Stack>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17, mt: 0.5 }}>Ordering Food Practice</Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
                    <LinearProgress variant="determinate" value={82} sx={{ flex: 1, height: 6, borderRadius: 99, backgroundColor: BORDER, '& .MuiLinearProgress-bar': { borderRadius: 99, backgroundColor: BLUE } }} />
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 12 }}>82%</Typography>
                </Stack>
                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mt: 0.75 }}>{activities.length} exercises · {totalPrompts} steps · {totalMinutes} min</Typography>
            </Box>
            <Divider />

            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                <Button onClick={() => onAction('Assignment overview opened in this static demo.')} sx={{ width: '100%', justifyContent: 'flex-start', color: SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 13, px: 1.25, py: 1.25, mb: 0.75 }}>
                    <MenuBookRoundedIcon sx={{ mr: 1.25, fontSize: 19 }} /> Assignment overview
                </Button>
                <Button
                    onClick={onSelectLanguage}
                    sx={{
                        width: '100%', justifyContent: 'flex-start', color: selectedActivity === null ? NAVY : SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 13, px: 1.25, py: 1.25, mb: 1,
                        borderRadius: '14px', borderLeft: `4px solid ${selectedActivity === null ? BLUE : 'transparent'}`,
                        backgroundColor: selectedActivity === null ? SOFT_BLUE : 'transparent',
                    }}
                >
                    <AutoAwesomeRoundedIcon sx={{ mr: 1.25, fontSize: 19, color: selectedActivity === null ? BLUE : SLATE }} /> Vocabulary & grammar
                    <Chip label="11" size="small" sx={{ ml: 'auto', height: 22, fontFamily: INTER, fontSize: 11, color: BLUE, backgroundColor: '#fff' }} />
                </Button>
                <Stack spacing={0.75}>
                    {activities.map((activity, index) => {
                        const Icon = activityIcons[activity.icon];
                        const isSelected = activity.id === selectedActivity?.id;
                        return (
                            <Box key={activity.id}>
                                <Button
                                    onClick={() => onSelectActivity(activity)}
                                    sx={{
                                        width: '100%', justifyContent: 'flex-start', textAlign: 'left', px: 1.25, py: 1.25,
                                        borderRadius: '14px', borderLeft: `4px solid ${isSelected ? BLUE : 'transparent'}`,
                                        backgroundColor: isSelected ? SOFT_BLUE : 'transparent',
                                        '&:hover': { backgroundColor: isSelected ? SOFT_BLUE : SURFACE_SUBTLE },
                                    }}
                                >
                                    <Box sx={{ width: 34, height: 34, borderRadius: '11px', flexShrink: 0, display: 'grid', placeItems: 'center', backgroundColor: isSelected ? '#fff' : SURFACE_MUTED }}>
                                        <Icon sx={{ color: isSelected ? BLUE : SLATE, fontSize: 19 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, ml: 1.25 }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: isSelected ? BLUE_TEXT : SLATE, fontSize: 11, letterSpacing: '0.05em' }}>EXERCISE {index + 1}</Typography>
                                            <Chip label="Interactive" size="small" sx={{ height: 22, fontFamily: INTER, fontSize: 11, color: SLATE, backgroundColor: '#fff' }} />
                                        </Stack>
                                        <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13 }}>{activity.title}</Typography>
                                        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5 }}>{activity.prompts.length} steps · {activity.duration} min</Typography>
                                    </Box>
                                    <Chip
                                        label={activity.status === 'ready' ? 'Ready' : activity.status === 'review' ? 'Review' : 'Draft'}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontFamily: INTER,
                                            fontWeight: 700,
                                            fontSize: 11,
                                            color: activity.status === 'ready' ? SUCCESS_TEXT : activity.status === 'review' ? WARNING_TEXT : BLUE_TEXT,
                                            backgroundColor: activity.status === 'ready' ? SUCCESS_SOFT : activity.status === 'review' ? `${YELLOW}45` : SOFT_BLUE,
                                        }}
                                    />
                                </Button>

                                {isSelected && (
                                    <Stack sx={{ pl: 4.5, pr: 1, py: 0.75 }}>
                                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11, letterSpacing: '0.06em', px: 1, mb: 0.5 }}>STEPS <Box component="span" sx={{ fontWeight: 500, color: MUTED }}>· Student turns</Box></Typography>
                                        {activity.prompts.map((prompt, promptIndex) => (
                                            <Button
                                                key={prompt.id}
                                                onClick={() => onSelectPrompt(activity, prompt)}
                                                sx={{
                                                    justifyContent: 'flex-start', textAlign: 'left', px: 1, py: 0.7, borderRadius: '9px',
                                                    color: selectedPromptId === prompt.id ? BLUE : SLATE,
                                                    backgroundColor: selectedPromptId === prompt.id ? '#fff' : 'transparent',
                                                    fontFamily: INTER, fontWeight: selectedPromptId === prompt.id ? 700 : 500, fontSize: 11.5,
                                                }}
                                            >
                                                <Typography component="span" sx={{ width: 18, flexShrink: 0, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: selectedPromptId === prompt.id ? BLUE : MUTED }}>{promptIndex + 1}</Typography>
                                                <Box component="span" sx={{ minWidth: 0 }}>
                                                    <Typography noWrap component="span" sx={{ display: 'block', fontFamily: INTER, fontWeight: 'inherit', fontSize: 11.5 }}>{prompt.title}</Typography>
                                                    <Typography noWrap component="span" sx={{ display: 'block', fontFamily: INTER, color: SLATE, fontSize: 11, mt: 0.1 }}>{prompt.detail} · Student turn</Typography>
                                                </Box>
                                            </Button>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: 1.5 }}>
                <Button fullWidth variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => onAction('A new exercise placeholder was added in this static demo.')} sx={{ borderColor: BORDER, color: BLUE, fontFamily: INTER, fontSize: 13, py: 1.25 }}>
                    Add exercise
                </Button>
            </Box>
        </Box>
    );
}

function StudentActivityPreview({ activity, prompt, onAction }: { activity: TeacherActivity; prompt: TeacherPrompt | null; onAction: (message: string) => void }) {
    return (
        <Box sx={{ ...cardSx, overflow: 'hidden', maxWidth: 720, mx: 'auto' }}>
            <Box sx={{ minHeight: 176, p: 3, position: 'relative', overflow: 'hidden', background: `linear-gradient(135deg, ${NAVY} 0%, ${BLUE} 100%)` }}>
                <Box sx={{ position: 'absolute', width: 190, height: 190, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)', right: -45, top: -70 }} />
                <Box sx={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', backgroundColor: `${YELLOW}38`, left: -20, bottom: -45 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Exercise · ${activity.title}`} size="small" sx={{ fontFamily: INTER, fontWeight: 600, color: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' }} />
                    {prompt && <Chip label={`Step · ${prompt.title}`} size="small" sx={{ fontFamily: INTER, fontWeight: 600, color: NAVY, backgroundColor: YELLOW }} />}
                </Stack>
                <Typography sx={{ maxWidth: 460, fontFamily: POPPINS, fontWeight: 600, color: '#fff', fontSize: 25, mt: 2, lineHeight: 1.3 }}>
                    你准备好点菜了吗？
                </Typography>
                <Typography sx={{ fontFamily: INTER, color: 'rgba(255,255,255,0.78)', fontSize: 14, mt: 0.75 }}>
                    Nǐ zhǔnbèi hǎo diǎn cài le ma?
                </Typography>
            </Box>
            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                        <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 16 }}>服务员</Typography>
                        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13 }}>Waiter</Typography>
                    </Box>
                    <IconButton aria-label="Play model audio" onClick={() => onAction('Model audio played in this static demo.')} sx={{ ml: 'auto !important', color: BLUE, border: `1px solid ${BORDER}` }}><PlayArrowRoundedIcon /></IconButton>
                </Stack>

                <Box sx={{ mt: 3, p: 2.25, borderRadius: '16px', backgroundColor: SURFACE_SUBTLE }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, color: NAVY, fontSize: 20, lineHeight: 1.5 }}>
                        您好！欢迎光临。您想吃什么？
                    </Typography>
                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13, mt: 0.75 }}>
                        Hello! Welcome. What would you like to eat?
                    </Typography>
                </Box>

                <Typography sx={{ fontFamily: INTER, fontWeight: 600, color: SLATE, fontSize: 12, mt: 3, mb: 1 }}>RESPONSE STARTERS</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {['请给我一盘……', '我要一碗……', '你们有什么……？'].map(starter => (
                        <Chip key={starter} label={starter} sx={{ fontFamily: POPPINS, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />
                    ))}
                </Stack>

                <Button fullWidth variant="contained" startIcon={<KeyboardVoiceRoundedIcon />} onClick={() => onAction('Recording started in this static student preview.')} sx={{ ...primaryButtonSx, mt: 3 }}>
                    Hold to answer
                </Button>
            </Box>
        </Box>
    );
}

function ActivityEditor({ activity, prompt, onAction }: { activity: TeacherActivity; prompt: TeacherPrompt | null; onAction: (message: string) => void }) {
    return (
        <Box sx={{ ...cardSx, maxWidth: 820, mx: 'auto', p: { xs: 2.5, md: 3.5 } }}>
            <Stack spacing={3}>
                {prompt && (
                    <Box sx={{ p: 1.75, borderRadius: '14px', backgroundColor: SOFT_BLUE }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE_TEXT, fontSize: 11.5, letterSpacing: '0.06em' }}>SELECTED STEP <Box component="span" sx={{ fontWeight: 500, color: SLATE }}>· Student turn</Box></Typography>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15, mt: 0.5 }}>{prompt.title}</Typography>
                        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12, mt: 0.25 }}>{prompt.detail}</Typography>
                    </Box>
                )}
                <Box>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>PEDAGOGICAL PURPOSE</Typography>
                    <TextField fullWidth defaultValue={activity.purpose} multiline sx={{ '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>TEACHER INSTRUCTIONS</Typography>
                    <TextField fullWidth defaultValue={activity.teacherInstructions} multiline minRows={3} sx={{ '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>STUDENT INSTRUCTIONS</Typography>
                    <TextField fullWidth defaultValue={activity.studentInstructions} multiline minRows={2} sx={{ '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 12, mb: 1 }}>TARGET LANGUAGE</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {activity.targetLanguage.map(item => <Chip key={item} label={item} onDelete={() => onAction(`${item} removed from this static activity.`)} sx={{ fontFamily: POPPINS, color: NAVY, backgroundColor: SOFT_BLUE }} />)}
                        <Chip icon={<AddRoundedIcon />} label="Add" clickable onClick={() => onAction('Target-language picker opened in this static demo.')} variant="outlined" sx={{ fontFamily: INTER, borderColor: BORDER, color: BLUE }} />
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

function LanguageSourceChip({ source }: { source: TargetSource }) {
    const labels: Record<TargetSource, string> = { required: 'Teacher required', suggested: 'AI suggested', added: 'Teacher added' };
    const colors: Record<TargetSource, string> = { required: NAVY, suggested: BLUE_TEXT, added: SUCCESS_TEXT };
    return <Chip label={labels[source]} size="small" sx={{ height: 22, fontFamily: INTER, fontWeight: 600, fontSize: 11, color: colors[source], backgroundColor: source === 'added' ? SUCCESS_SOFT : SOFT_BLUE }} />;
}

function LanguageTargetsEditor({ onAction }: { onAction: (message: string) => void }) {
    const [tab, setTab] = React.useState<'vocabulary' | 'grammar'>('vocabulary');

    return (
        <Box sx={{ ...cardSx, maxWidth: 920, mx: 'auto', overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2.5, md: 3.5 }, pb: 2 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 19 }}>Language targets</Typography>
                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5, mt: 0.5 }}>These approved targets connect the printable plan to every runnable activity.</Typography>
                <ToggleButtonGroup exclusive size="small" value={tab} onChange={(_event, value) => value && setTab(value)} sx={{ mt: 2.5 }}>
                    <ToggleButton value="vocabulary" sx={{ fontFamily: INTER, px: 2.5 }}>Vocabulary · {vocabularyTargets.length}</ToggleButton>
                    <ToggleButton value="grammar" sx={{ fontFamily: INTER, px: 2.5 }}>Grammar · {grammarTargets.length}</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Divider />

            <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                {tab === 'vocabulary' ? (
                    <Stack spacing={1}>
                        {vocabularyTargets.map(target => (
                            <Box key={target.hanzi} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '100px 130px minmax(100px, 1fr) auto' }, gap: 1.5, alignItems: 'center', p: 1.5, borderRadius: '14px', border: `1px solid ${BORDER}`, backgroundColor: SURFACE_TINT }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 17 }}>{target.hanzi}</Typography>
                                <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontFamily: INTER, color: BLUE, fontSize: 13 }}>{target.pinyin}</Typography>
                                <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontFamily: INTER, color: SLATE, fontSize: 13 }}>{target.meaning}{target.isReview ? ' · Review' : ''}</Typography>
                                <LanguageSourceChip source={target.source} />
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Stack spacing={1.25}>
                        {grammarTargets.map(target => (
                            <Box key={target.pattern} sx={{ p: 2, borderRadius: '14px', border: `1px solid ${BORDER}`, backgroundColor: SURFACE_TINT }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                    <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 16 }}>{target.pattern}</Typography>
                                    <LanguageSourceChip source={target.source} />
                                </Stack>
                                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13, mt: 0.75 }}>{target.meaning}</Typography>
                                <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '10px', backgroundColor: SOFT_BLUE }}>
                                    <Typography sx={{ fontFamily: POPPINS, color: BLUE_TEXT, fontSize: 14 }}>{target.example}</Typography>
                                </Box>
                                <Typography sx={{ fontFamily: INTER, color: SUCCESS_TEXT, fontWeight: 600, fontSize: 11.5, mt: 1.25 }}>Used in 4 activities · Taught before assessment</Typography>
                            </Box>
                        ))}
                    </Stack>
                )}

                <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => onAction(`${tab === 'vocabulary' ? 'Vocabulary' : 'Grammar'} editor opened in this static demo.`)} sx={{ mt: 2, borderColor: BORDER, color: BLUE, fontFamily: INTER, fontSize: 13 }}>
                    Add {tab === 'vocabulary' ? 'vocabulary' : 'grammar point'}
                </Button>
            </Box>
        </Box>
    );
}

function CopilotPanel({ activity, prompt, onAction, embedded = false }: { activity: TeacherActivity | null; prompt: TeacherPrompt | null; onAction: (message: string) => void; embedded?: boolean }) {
    const [suggestion, setSuggestion] = React.useState<'idle' | 'proposed' | 'applied'>('idle');

    React.useEffect(() => setSuggestion('idle'), [activity?.id, prompt?.id]);

    const contextName = prompt?.title ?? activity?.title ?? 'Vocabulary & grammar';

    return (
        <Box component="aside" sx={{ minWidth: 0, minHeight: 0, height: '100%', display: embedded ? 'flex' : { xs: 'none', xl: 'flex' }, flexDirection: 'column', backgroundColor: '#fff', borderLeft: embedded ? 0 : `1px solid ${BORDER}` }}>
            <Box sx={{ p: 2.25 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{ width: 36, height: 36, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                        <AutoAwesomeRoundedIcon sx={{ color: BLUE, fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15 }}>Lesson copilot</Typography>
                        <Typography sx={{ fontFamily: INTER, color: SUCCESS_TEXT, fontSize: 12 }}>{prompt ? 'Editing step' : activity ? 'Editing exercise' : 'Editing lesson targets'} · {contextName}</Typography>
                    </Box>
                    <IconButton size="small" aria-label="More copilot options" onClick={() => onAction('Copilot options opened in this static demo.')} sx={{ color: SLATE }}><MoreHorizRoundedIcon /></IconButton>
                </Stack>
            </Box>
            <Divider />

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2.25 }}>
                <Box sx={{ backgroundColor: SOFT_BLUE, borderRadius: '16px 16px 16px 4px', p: 2 }}>
                    <Typography sx={{ fontFamily: INTER, color: NAVY, fontSize: 13.5, lineHeight: 1.55 }}>
                        {activity
                            ? `This ${prompt ? 'step' : 'exercise'} belongs to “${activity.title}” and contains ${activity.prompts.length} student turns. Edit it directly, or ask me to revise it without changing the rest of the assignment.`
                            : 'Edit the language targets directly, or ask me to suggest level-appropriate language, identify missing coverage, or simplify the grammar sequence.'}
                    </Typography>
                </Box>

                <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11, letterSpacing: '0.06em', mt: 3, mb: 1 }}>QUICK ACTIONS</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {['Make easier', 'Add scaffolding', 'More speaking', 'Shorten'].map(action => (
                        <Chip key={action} label={action} onClick={() => setSuggestion('proposed')} sx={{ fontFamily: INTER, fontSize: 12, color: BLUE, backgroundColor: SURFACE_SUBTLE, border: `1px solid ${BORDER}` }} />
                    ))}
                </Stack>

                {suggestion === 'proposed' && (
                    <Box sx={{ mt: 2.5, p: 2, borderRadius: '16px', border: `1px solid ${BORDER}`, backgroundColor: '#fff' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AutoAwesomeRoundedIcon sx={{ color: BLUE, fontSize: 18 }} />
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 13 }}>Suggested change</Typography>
                        </Stack>
                        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13, lineHeight: 1.55, mt: 1.25 }}>
                            {activity
                                ? 'Add three response starters and let students hear the waiter’s example before recording.'
                                : 'Add 点菜 as a review word and keep the lesson at eight new vocabulary items.'}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                            <Button onClick={() => setSuggestion('applied')} sx={{ ...aiButtonSx, flex: 1, py: 0.9, fontSize: 12 }}>Apply</Button>
                            <Button onClick={() => setSuggestion('idle')} sx={{ flex: 1, py: 0.9, color: SLATE, fontFamily: INTER, fontSize: 12 }}>Dismiss</Button>
                        </Stack>
                    </Box>
                )}

                {suggestion === 'applied' && (
                    <Box sx={{ mt: 2.5, p: 2, borderRadius: '16px', backgroundColor: SUCCESS_SOFT, display: 'flex', gap: 1 }}>
                        <CheckRoundedIcon sx={{ color: GREEN, fontSize: 19 }} />
                        <Typography sx={{ fontFamily: INTER, color: SUCCESS_TEXT, fontWeight: 600, fontSize: 13 }}>Suggestion applied to this mockup.</Typography>
                    </Box>
                )}
            </Box>

            <Divider />
            <Box sx={{ p: 2 }}>
                <TextField
                    fullWidth multiline maxRows={4}
                    placeholder={`Ask AI about ${contextName}…`}
                    InputProps={{ endAdornment: <Tooltip title="Static mockup"><span><IconButton disabled sx={{ color: BLUE }}><SendRoundedIcon /></IconButton></span></Tooltip> }}
                    sx={{ '& .MuiOutlinedInput-root': { fontFamily: INTER, fontSize: 13.5, backgroundColor: SURFACE_TINT } }}
                />
                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, textAlign: 'center', mt: 1 }}>AI chat is illustrative in this wireframe.</Typography>
            </Box>
        </Box>
    );
}

export default function LessonExercisesEditor({ activities, selectedActivity, onSelectActivity, onSelectLanguage, onBack, onFinish }: TeacherBuilderProps) {
    const [mode, setMode] = React.useState<'edit' | 'preview'>('preview');
    const [selectedPromptId, setSelectedPromptId] = React.useState<number | null>(selectedActivity?.prompts[0]?.id ?? null);
    const [structureOpen, setStructureOpen] = React.useState(false);
    const [copilotOpen, setCopilotOpen] = React.useState(false);
    const [message, setMessage] = React.useState<string | null>(null);

    const promptLocations = React.useMemo(
        () => activities.flatMap(activity => activity.prompts.map(prompt => ({ activity, prompt }))),
        [activities],
    );
    const selectedPrompt = selectedActivity?.prompts.find(prompt => prompt.id === selectedPromptId) ?? selectedActivity?.prompts[0] ?? null;
    const selectedPromptGlobalIndex = selectedPrompt ? promptLocations.findIndex(location => location.prompt.id === selectedPrompt.id) : -1;
    const selectedPromptLocalIndex = selectedPrompt && selectedActivity ? selectedActivity.prompts.findIndex(prompt => prompt.id === selectedPrompt.id) : -1;

    const handleSelectExercise = (activity: TeacherActivity) => {
        onSelectActivity(activity);
        setSelectedPromptId(activity.prompts[0]?.id ?? null);
        setStructureOpen(false);
    };

    const handleSelectPrompt = (activity: TeacherActivity, prompt: TeacherPrompt) => {
        onSelectActivity(activity);
        setSelectedPromptId(prompt.id);
        setStructureOpen(false);
    };

    const handleSelectLanguage = () => {
        onSelectLanguage();
        setStructureOpen(false);
    };

    const handleBrowsePrompt = (direction: -1 | 1) => {
        const target = promptLocations[selectedPromptGlobalIndex + direction];
        if (!target) return;
        onSelectActivity(target.activity);
        setSelectedPromptId(target.prompt.id);
    };

    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '270px minmax(0, 1fr)', xl: '300px minmax(520px, 1fr) 360px' }, overflow: 'hidden' }}>
            <LessonOutlinePanel
                activities={activities}
                selectedActivity={selectedActivity}
                onSelectActivity={handleSelectExercise}
                onSelectLanguage={handleSelectLanguage}
                selectedPromptId={selectedPrompt?.id ?? null}
                onSelectPrompt={handleSelectPrompt}
                onAction={setMessage}
            />

            <Box component="main" sx={{ minWidth: 0, minHeight: 0, overflowY: 'auto', p: { xs: 2, lg: 3 } }}>
                <Box sx={{ maxWidth: 920, mx: 'auto' }}>
                    <Stack direction="row" spacing={1} sx={{ display: { xs: 'flex', xl: 'none' }, mb: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<MenuBookRoundedIcon />}
                            onClick={() => setStructureOpen(true)}
                            sx={{ display: { xs: 'inline-flex', md: 'none' }, flex: 1, borderColor: BORDER, color: BLUE, fontFamily: INTER, fontWeight: 700 }}
                        >
                            Lesson structure
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<AutoAwesomeRoundedIcon />}
                            onClick={() => setCopilotOpen(true)}
                            sx={{ flex: 1, borderColor: BORDER, color: BLUE, fontFamily: INTER, fontWeight: 700 }}
                        >
                            Copilot
                        </Button>
                    </Stack>

                    <Box sx={{ mb: 2, px: 1, display: 'flex', alignItems: 'center', gap: 0.65, flexWrap: 'wrap' }}>
                        {[
                            ['Course', 'Intermediate Mandarin II'],
                            ['Assignment', 'Ordering Food Practice'],
                            ...(selectedActivity ? [['Exercise', selectedActivity.title]] : []),
                            ...(selectedPrompt ? [['Step', selectedPrompt.title]] : []),
                        ].map(([type, label], index, items) => (
                            <React.Fragment key={`${type}-${label}`}>
                                <Chip label={type} size="small" sx={{ height: 22, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: type === 'Step' ? BLUE_TEXT : SLATE, backgroundColor: type === 'Step' ? SOFT_BLUE : '#fff' }} />
                                <Typography sx={{ fontFamily: INTER, color: type === 'Step' ? NAVY : SLATE, fontWeight: type === 'Step' ? 700 : 500, fontSize: 11.5 }}>{label}</Typography>
                                {index < items.length - 1 && <ChevronRightRoundedIcon sx={{ color: MUTED, fontSize: 15 }} />}
                            </React.Fragment>
                        ))}
                    </Box>

                    {selectedActivity === null ? (
                        <>
                            <Box sx={{ mb: 2.5 }}>
                                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: { xs: 23, md: 27 }, letterSpacing: '-0.02em' }}>Vocabulary & grammar</Typography>
                                <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5, mt: 0.5 }}>8 vocabulary items · 3 grammar points · All lesson activities covered</Typography>
                            </Box>
                            <LanguageTargetsEditor onAction={setMessage} />
                        </>
                    ) : (
                        <>
                            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2.5 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip label="Exercise" size="small" sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />
                                        <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: { xs: 23, md: 27 }, letterSpacing: '-0.02em' }}>{selectedActivity.title}</Typography>
                                        <Chip label={`${selectedActivity.duration} min`} size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: '#fff', border: `1px solid ${BORDER}` }} />
                                    </Stack>
                                    <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13.5, mt: 0.5 }}>{selectedActivity.type} · {selectedActivity.prompts.length} student turns <Box component="span" sx={{ color: MUTED }}>· runnable exercise</Box></Typography>
                                </Box>
                                <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_event, value) => value && setMode(value)}>
                                    <ToggleButton value="edit" sx={{ fontFamily: INTER, px: 2 }}>Edit</ToggleButton>
                                    <ToggleButton value="preview" sx={{ fontFamily: INTER, px: 2 }}>Student preview</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>

                            {selectedPrompt && (
                                <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '16px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.25, backgroundColor: '#fff', border: `1px solid ${BORDER}` }}>
                                    <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 700, color: BLUE_TEXT, fontSize: 12 }}>{selectedPromptLocalIndex + 1}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE, fontSize: 11, letterSpacing: '0.06em' }}>STEP {selectedPromptLocalIndex + 1} OF {selectedActivity.prompts.length}</Typography>
                                            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11 }}>· Student turn · {selectedPromptGlobalIndex + 1} of {promptLocations.length} in assignment</Typography>
                                        </Stack>
                                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 14, mt: 0.25 }}>{selectedPrompt.title}</Typography>
                                        <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, mt: 0.15 }}>{selectedPrompt.detail}</Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.75}>
                                        <Tooltip title="Previous step">
                                            <span>
                                                <Button disabled={selectedPromptGlobalIndex <= 0} onClick={() => handleBrowsePrompt(-1)} startIcon={<ChevronLeftRoundedIcon />} sx={{ border: `1px solid ${BORDER}`, color: BLUE, fontFamily: INTER, fontSize: 11.5, px: 1.25, py: 0.8 }}>Previous</Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Next step">
                                            <span>
                                                <Button disabled={selectedPromptGlobalIndex >= promptLocations.length - 1} onClick={() => handleBrowsePrompt(1)} endIcon={<ChevronRightRoundedIcon />} sx={{ border: `1px solid ${BORDER}`, color: BLUE, fontFamily: INTER, fontSize: 11.5, px: 1.25, py: 0.8 }}>Next</Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                            )}

                            {mode === 'preview'
                                ? <StudentActivityPreview activity={selectedActivity} prompt={selectedPrompt} onAction={setMessage} />
                                : <ActivityEditor activity={selectedActivity} prompt={selectedPrompt} onAction={setMessage} />}
                        </>
                    )}

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ sm: 'center' }}
                        spacing={1}
                        sx={{ position: 'sticky', bottom: 0, zIndex: 2, mt: 3, p: 1.5, borderRadius: '16px', border: `1px solid ${BORDER}`, backgroundColor: 'rgba(246,248,252,0.96)', backdropFilter: 'blur(8px)' }}
                    >
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1 }}>
                            <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 18 }} />
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SUCCESS_TEXT }}>All changes auto-saved</Typography>
                        </Stack>
                        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ color: SLATE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Back to lesson plan</Button>
                        <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={onFinish} sx={{ ...primaryButtonSx, borderRadius: '11px', px: 2.5 }}>Review & publish</Button>
                    </Stack>
                </Box>
            </Box>

            <CopilotPanel activity={selectedActivity} prompt={selectedPrompt} onAction={setMessage} />

            <Drawer
                anchor="left"
                open={structureOpen}
                onClose={() => setStructureOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: 'min(88vw, 340px)' } }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                        <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Lesson structure</Typography>
                        <IconButton aria-label="Close lesson structure" onClick={() => setStructureOpen(false)} sx={{ color: SLATE }}><CloseRoundedIcon /></IconButton>
                    </Stack>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <LessonOutlinePanel
                            embedded
                            activities={activities}
                            selectedActivity={selectedActivity}
                            onSelectActivity={handleSelectExercise}
                            onSelectLanguage={handleSelectLanguage}
                            selectedPromptId={selectedPrompt?.id ?? null}
                            onSelectPrompt={handleSelectPrompt}
                            onAction={setMessage}
                        />
                    </Box>
                </Box>
            </Drawer>

            <Drawer
                anchor="right"
                open={copilotOpen}
                onClose={() => setCopilotOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: 'min(92vw, 380px)' } }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                        <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Lesson copilot</Typography>
                        <IconButton aria-label="Close lesson copilot" onClick={() => setCopilotOpen(false)} sx={{ color: SLATE }}><CloseRoundedIcon /></IconButton>
                    </Stack>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <CopilotPanel embedded activity={selectedActivity} prompt={selectedPrompt} onAction={setMessage} />
                    </Box>
                </Box>
            </Drawer>

            <Snackbar open={Boolean(message)} autoHideDuration={2600} onClose={() => setMessage(null)} message={message} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} ContentProps={{ sx: { borderRadius: '12px', backgroundColor: NAVY, fontFamily: INTER, fontSize: 13 } }} />
        </Box>
    );
}
